import { useState, useEffect, useCallback } from 'react';
import { getCalendarEvents, createCalendarEvent, deleteCalendarEvent, getDocuments } from '../services/api';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import { de } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, FileText, Trash2, X, LayoutGrid, List, Wallet, Pin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import Sidebar from '../components/layout/Sidebar';

const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', start: '', start_time: '', event_type: 'deadline' });

  const loadData = useCallback(async () => {
    try {
      const [eventsRes, docsRes] = await Promise.all([
        getCalendarEvents(),
        getDocuments()
      ]);
      
      const calEvents = eventsRes.data;
      const docDeadlines = docsRes.data
        .filter(d => d.due_date && d.status !== 'done')
        .map(d => ({
          id: `doc-${d.id}`,
          title: `${d.sender_name || 'Rechnung'} - ${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(d.amount || 0)}`,
          start: d.due_date,
          event_type: 'document',
          document_id: d.id
        }));
      
      setEvents([...calEvents, ...docDeadlines]);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.start) {
      toast.error('Bitte Titel und Datum eingeben');
      return;
    }

    try {
      const eventData = {
        ...newEvent,
        start: newEvent.start_time ? `${newEvent.start}T${newEvent.start_time}` : newEvent.start
      };
      await createCalendarEvent(eventData);
      toast.success('Termin erstellt!');
      setNewEventOpen(false);
      setNewEvent({ title: '', start: '', start_time: '', event_type: 'deadline' });
      loadData();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (eventId.startsWith('doc-')) {
      toast.info('Dokument-Fristen können im Board geändert werden');
      return;
    }

    try {
      await deleteCalendarEvent(eventId);
      toast.success('Termin gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const navigatePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getDays = () => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
      
      const startDay = monthStart.getDay();
      const paddingDays = startDay === 0 ? 6 : startDay - 1;
      return Array(paddingDays).fill(null).concat(days);
    } else {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    }
  };

  const days = getDays();

  const getEventsForDay = (day) => {
    if (!day) return [];
    return events.filter(e => isSameDay(new Date(e.start), day));
  };

  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  const getEventIcon = (eventType) => {
    return eventType === 'document' ? Wallet : Pin;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative overflow-hidden ${document.documentElement.classList.contains('dark') ? 'galaxy-bg' : 'bg-background'}`} data-testid="calendar-page">
      {/* Light Mode Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none dark:hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <div className="flex relative z-10">
        <Sidebar />

        <main className="flex-1 min-h-screen">
          {/* Header */}
          <header className="glass-panel sticky top-0 z-40 border-b border-white/10 dark:bg-white/5 dark:border-white/10">
            <div className="px-6 py-4 flex items-center justify-between">
              <h1 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Kalender
              </h1>

              <div className="flex items-center gap-3">
                {/* View Mode Toggle */}
                <div className="flex glass-card rounded-xl p-1 dark:bg-white/5">
                  <button
                    onClick={() => setViewMode('month')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      viewMode === 'month' 
                        ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <LayoutGrid className="w-4 h-4 inline mr-1" />
                    Monat
                  </button>
                  <button
                    onClick={() => setViewMode('week')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      viewMode === 'week' 
                        ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <List className="w-4 h-4 inline mr-1" />
                    Woche
                  </button>
                </div>

                <Button variant="outline" onClick={goToToday} className="rounded-xl glass-input dark:bg-white/10 dark:border-white/20">
                  Heute
                </Button>

                <Dialog open={newEventOpen} onOpenChange={setNewEventOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl gap-2 shadow-lg shadow-violet-500/25">
                      <Plus className="w-4 h-4" />
                      Neuer Termin
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-panel border-white/20 dark:bg-gray-900/95 dark:border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Neuen Termin erstellen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label className="text-foreground">Titel</Label>
                        <Input
                          value={newEvent.title}
                          onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                          placeholder="z.B. Rechnung prüfen"
                          className="mt-2 rounded-xl glass-input dark:bg-white/10 dark:border-white/20 text-foreground"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-foreground">Datum</Label>
                          <Input
                            type="date"
                            value={newEvent.start}
                            onChange={(e) => setNewEvent({ ...newEvent, start: e.target.value })}
                            className="mt-2 rounded-xl glass-input dark:bg-white/10 dark:border-white/20 text-foreground"
                          />
                        </div>
                        <div>
                          <Label className="text-foreground">Uhrzeit</Label>
                          <Input
                            type="time"
                            value={newEvent.start_time}
                            onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                            className="mt-2 rounded-xl glass-input dark:bg-white/10 dark:border-white/20 text-foreground"
                          />
                        </div>
                      </div>
                      <Button onClick={handleCreateEvent} className="w-full bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl shadow-lg shadow-violet-500/25">
                        Termin erstellen
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </header>

          {/* Calendar */}
          <div className="p-6 md:p-8">
            <div className="glass-card rounded-3xl overflow-hidden dark:bg-white/5 dark:border-white/10">
              {/* Month/Week Navigation */}
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/5">
                <button
                  onClick={navigatePrev}
                  className="p-2 hover:bg-white/20 dark:hover:bg-white/10 rounded-xl transition-colors text-muted-foreground hover:text-foreground"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {viewMode === 'month' 
                      ? format(currentDate, 'MMMM yyyy', { locale: de })
                      : `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd. MMM', { locale: de })} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'd. MMM yyyy', { locale: de })}`
                    }
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {events.filter(e => {
                      const eventDate = new Date(e.start);
                      if (viewMode === 'month') {
                        return isSameMonth(eventDate, currentDate);
                      } else {
                        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
                        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
                        return eventDate >= weekStart && eventDate <= weekEnd;
                      }
                    }).length} Termine
                  </p>
                </div>
                <button
                  onClick={navigateNext}
                  className="p-2 hover:bg-white/20 dark:hover:bg-white/10 rounded-xl transition-colors text-muted-foreground hover:text-foreground"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Week Days Header */}
              <div className={`grid ${viewMode === 'week' ? 'grid-cols-7' : 'grid-cols-7'} border-b border-white/10 dark:border-white/5`}>
                {weekDays.map((day) => (
                  <div key={day} className="p-4 text-center text-sm font-medium text-muted-foreground">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className={`grid grid-cols-7`}>
                {days.map((day, index) => {
                  const dayEvents = day ? getEventsForDay(day) : [];
                  const isToday = day && isSameDay(day, new Date());
                  const isSelected = day && selectedDate && isSameDay(day, selectedDate);
                  const isCurrentMonth = viewMode === 'month' ? (day && isSameMonth(day, currentDate)) : true;

                  return (
                    <div
                      key={index}
                      onClick={() => day && setSelectedDate(day)}
                      className={`${viewMode === 'week' ? 'min-h-[250px]' : 'min-h-[120px]'} p-3 border-b border-r border-white/5 dark:border-white/5 cursor-pointer transition-colors ${
                        !day ? 'bg-white/[0.02] dark:bg-white/[0.01]' :
                        !isCurrentMonth ? 'bg-white/[0.01] dark:bg-white/[0.01]' :
                        isSelected ? 'bg-indigo-500/10' :
                        'hover:bg-white/10 dark:hover:bg-white/5'
                      }`}
                    >
                      {day && (
                        <>
                          <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl text-base font-medium ${
                            isToday 
                              ? 'bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/30' 
                              : isCurrentMonth ? 'text-foreground' : 'text-muted-foreground'
                          }`}>
                            {format(day, 'd')}
                          </span>
                          <div className="mt-2 space-y-1.5">
                            {dayEvents.slice(0, viewMode === 'week' ? 5 : 3).map((event) => {
                              const EventIcon = getEventIcon(event.event_type);
                              return (
                                <div
                                  key={event.id}
                                  className={`text-xs px-2.5 py-1.5 rounded-lg truncate flex items-center gap-1.5 ${
                                    event.event_type === 'document' 
                                      ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400' 
                                      : 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-400'
                                  }`}
                                >
                                  <EventIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="truncate">{event.title}</span>
                                </div>
                              );
                            })}
                            {dayEvents.length > (viewMode === 'week' ? 5 : 3) && (
                              <span className="text-xs text-muted-foreground px-2">
                                +{dayEvents.length - (viewMode === 'week' ? 5 : 3)} mehr
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Selected Day Events */}
            <AnimatePresence>
              {selectedDate && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-6 glass-card rounded-2xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {format(selectedDate, 'EEEE, d. MMMM', { locale: de })}
                    </h3>
                    <button onClick={() => setSelectedDate(null)} className="p-2 hover:bg-white/20 dark:hover:bg-white/10 rounded-lg text-muted-foreground hover:text-foreground">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {getEventsForDay(selectedDate).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p>Keine Termine an diesem Tag</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {getEventsForDay(selectedDate).map((event) => {
                        const EventIcon = getEventIcon(event.event_type);
                        return (
                          <div
                            key={event.id}
                            className={`flex items-center justify-between p-4 rounded-xl ${
                              event.event_type === 'document' ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-indigo-500/10 border border-indigo-500/20'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <EventIcon className={`w-5 h-5 ${event.event_type === 'document' ? 'text-amber-500' : 'text-indigo-500'}`} />
                              <span className="font-medium text-foreground">{event.title}</span>
                            </div>
                            {!event.id.startsWith('doc-') && (
                              <button
                                onClick={() => handleDeleteEvent(event.id)}
                                className="p-2 hover:bg-red-500/20 rounded-lg text-muted-foreground hover:text-red-500 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CalendarPage;
